/**
 * AnnotationVisual - Pure SVG visual component for rendering annotation blocks.
 *
 * Renders text labels/annotations on the canvas.
 * Pure visual component - no state or handlers.
 */

import type { BlockVisualProps } from '@/components/blocks/Types'
import type { AnnotationBlock } from '@/types/blocks/canvas/Annotation'
import { useBlockRenderContext } from '@/components/blocks/BlockRenderContext'
import { extractPoint } from '@/utils/Block.utils'

export function AnnotationVisual({ block, animatedPosition }: BlockVisualProps) {
	// Get scale factors and coordinate transform from context - must be called before any conditional returns
	const { scale, coordinateTransform } = useBlockRenderContext()
	const fontScale = scale?.playerFont ?? 1.0

	// Type guard
	if (block.type !== 'annotation') {
		console.error('AnnotationVisual called with non-annotation block:', block.type)
		return null
	}

	const annotationBlock = block as AnnotationBlock
	const { properties } = annotationBlock
	const { text, color, fontSize, offsetX, offsetY } = properties

	// Determine position - use animatedPosition if provided, otherwise extract from properties
	// Apply coordinate transform for field-to-viewBox conversion (Y-flip)
	const basePosition = extractPoint(annotationBlock)
	const rawX = animatedPosition && 'x' in animatedPosition
		? animatedPosition.x
		: basePosition?.x ?? 0
	const rawY = animatedPosition && 'y' in animatedPosition
		? animatedPosition.y
		: basePosition?.y ?? 0
	// Apply offsets before transform (offsets are in field coordinates)
	const transformed = coordinateTransform({ x: rawX + (offsetX ?? 0), y: rawY + (offsetY ?? 0) })
	const x = transformed.x
	const y = transformed.y

	// Calculate scaled font size
	const scaledFontSize = (fontSize || 12) * fontScale

	return (
		<text
			x={x}
			y={y}
			fill={color || 'var(--foreground)'}
			fontSize={scaledFontSize}
			fontFamily="var(--font-sans)"
			textAnchor="middle"
			dominantBaseline="middle"
			style={{ pointerEvents: 'none', userSelect: 'none' }}
		>
			{text}
		</text>
	)
}
